/**
 * QRMaps Design System - Button Styled Component
 *
 * Flexible button component with variants and sizes
 */

import styled, { css } from 'styled-components';
import { resetButton, focusVisible } from '../../utils/styled';

const variantStyles = {
  primary: css`
    background-color: ${({ theme }) => theme.colors.primary[500]};
    color: ${({ theme }) => theme.colors.neutral[0]};
    border-color: ${({ theme }) => theme.colors.primary[500]};

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.primary[600]};
      border-color: ${({ theme }) => theme.colors.primary[600]};
    }

    &:active:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.primary[700]};
    }
  `,

  secondary: css`
    background-color: ${({ theme }) => theme.colors.neutral[0]};
    color: ${({ theme }) => theme.colors.neutral[900]};
    border-color: ${({ theme }) => theme.colors.neutral[300]};

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.neutral[100]};
    }

    &:active:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.neutral[200]};
    }
  `,

  success: css`
    background-color: ${({ theme }) => theme.colors.success};
    color: ${({ theme }) => theme.colors.neutral[0]};
    border-color: ${({ theme }) => theme.colors.success};

    &:hover:not(:disabled) {
      background-color: #45a049;
    }
  `,

  danger: css`
    background-color: ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.neutral[0]};
    border-color: ${({ theme }) => theme.colors.error};

    &:hover:not(:disabled) {
      background-color: #c62828;
    }
  `,

  ghost: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.primary[500]};
    border-color: transparent;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.neutral[100]};
    }
  `,
};

const sizeStyles = {
  sm: css`
    width: ${({ $circular, theme }) => $circular ? theme.components.button.size.sm : 'auto'};
    height: ${({ theme }) => theme.components.button.size.sm};
    padding: ${({ $circular }) => $circular ? 0 : '0 12px'};
    font-size: ${({ theme }) => theme.components.button.iconSize.sm};
  `,

  md: css`
    width: ${({ $circular, theme }) => $circular ? theme.components.button.size.md : 'auto'};
    height: ${({ theme }) => theme.components.button.size.md};
    padding: ${({ $circular }) => $circular ? 0 : '0 16px'};
    font-size: ${({ theme }) => theme.components.button.iconSize.md};
  `,

  lg: css`
    width: ${({ $circular, theme }) => $circular ? theme.components.button.size.lg : 'auto'};
    height: ${({ theme }) => theme.components.button.size.lg};
    padding: ${({ $circular }) => $circular ? 0 : '0 24px'};
    font-size: ${({ theme }) => theme.components.button.iconSize.lg};
  `,
};

export const StyledButton = styled.button`
  ${resetButton}

  /* Layout */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  position: ${({ $position }) => $position || 'relative'};

  /* Styling */
  border-width: 2px;
  border-style: solid;
  border-radius: ${({ $circular, theme }) =>
    $circular ? theme.components.button.borderRadius : theme.spacing[1]};
  box-shadow: ${({ theme }) => theme.shadows.md};

  /* Typography */
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  line-height: 1;
  text-align: center;

  /* Interaction */
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: ${({ theme }) => theme.transitions.normal};
  user-select: none;
  opacity: ${({ disabled }) => disabled ? 0.6 : 1};

  /* Variants */
  ${({ $variant = 'secondary' }) => variantStyles[$variant]}

  /* Sizes */
  ${({ $size = 'md' }) => sizeStyles[$size]}

  /* Hover & Active states */
  &:hover:not(:disabled) {
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  /* Focus styles */
  ${({ theme }) => focusVisible(theme.colors.primary[500])}

  /* Positioning props */
  ${({ $position, $top, $right, $bottom, $left, theme }) =>
    $position === 'absolute' && css`
      top: ${$top};
      right: ${$right};
      bottom: ${$bottom};
      left: ${$left};
    `
  }

  /* Z-index */
  z-index: ${({ $zIndex, theme }) => $zIndex || theme.zIndex.base};

  /* Full width option */
  ${({ $fullWidth }) => $fullWidth && css`
    width: 100%;
  `}
`;